import { Button, Modal, useWindowDimensions } from '@insurancemenu/imenu-components-lib';
import {
  ProductLineSectionType,
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { useRootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useHistory } from 'react-router-dom';
import { toJS } from 'mobx';
import { RATE_SEGMENT_INFO } from 'global/utils/RateSegmentHelper';
import { useQueryParams } from 'global/hooks/useQuery';
import ProductLineCard from 'components/common/general/ProductLineCard';
import { ChevronUp, ChevronDown } from '@insurancemenu/imenu-components-lib/dist/icons';
import { createStyles } from './styles';

interface Props {
  show: boolean;
  cartId?: string;
  onClose: () => void;
}

const ProductDetailsModal = observer(({ show, cartId, onClose }: Props): JSX.Element => {
  // Mobx store
  const {
    rateSegmentsStore: { currentRateSegment, productLines },
  } = useRootStore();

  const [showAncillaryPLines, setShowAncillaryPLines] = useState(false);

  const classes = createStyles(showAncillaryPLines);
  // Show different layout for mobile and tablet view
  const { width: windowWidth } = useWindowDimensions();
  const isXSMobileView = windowWidth <= 400;

  const query = useQueryParams();
  const history = useHistory();
  const cartIdFromURL = query.get('cartId') || null;
  const groupId = query.get('groupId') || '';
  const currentCartId = cartId || cartIdFromURL;

  const scrollRef = useRef<HTMLDivElement>(null);

  const convertedProductLines = productLines.map((line) => toJS(line)).slice();
  function onModalClose(): void {
    onClose();
    setShowAncillaryPLines(false);
    scrollRef.current?.scrollTo(0, 0);
  }

  const onProductLineClick = (e: React.SyntheticEvent<HTMLLIElement>): void => {
    const selectedProductLineId = e.currentTarget.dataset.productLine;
    if (currentCartId && selectedProductLineId) {
      history.push(
        `/popular-plans?groupId=${groupId}&cartId=${currentCartId}&productLineId=${selectedProductLineId}&cartType=popular`,
      );
      onModalClose();
    }
  };

  const filterPLineByType = (productLineIds: ProductLineId[], type: ProductLineSectionType): ProductLineId[] => {
    return productLineIds.filter((productLine) => PRODUCT_LINES_CONTENT_MAP[productLine]?.section === type);
  };

  function getProductLinesButtons(productLineIds: ProductLineId[]): JSX.Element[] {
    return filterPLineByType(productLineIds, ProductLineSectionType.GENERAL)
      .sort(sortByProductLineId)
      .map((pLineId) => (
        <ProductLineCard
          key={pLineId}
          pLineId={pLineId}
          onCardClick={onProductLineClick}
          icon={PRODUCT_LINES_CONTENT_MAP[pLineId]?.icon('4rem')}
          label={PRODUCT_LINES_CONTENT_MAP[pLineId]?.label}
        />
      ));
  }

  function getAncillaryProductLinesButtons(productLineIds: ProductLineId[]): JSX.Element[] {
    return filterPLineByType(productLineIds, ProductLineSectionType.ANCILLARY)
      .sort(sortByProductLineId)
      .map((pLineId) => (
        <ProductLineCard
          key={pLineId}
          pLineId={pLineId}
          onCardClick={onProductLineClick}
          icon={PRODUCT_LINES_CONTENT_MAP[pLineId]?.icon('4rem')}
          label={PRODUCT_LINES_CONTENT_MAP[pLineId]?.label}
          className={classes.ancillaryProductLineButton}
        />
      ));
  }
  const getAncillaryButtonText = (): string => {
    if (isXSMobileView) {
      return showAncillaryPLines ? 'Show less' : 'Show more';
    }
    return showAncillaryPLines ? 'Hide Ancillary Product lines' : 'Show Ancillary Product lines';
  };
  const pLineIds = convertedProductLines
    .filter(({ rateSegment }) => rateSegment === currentRateSegment)
    .map(({ productLineId }) => productLineId);

  return (
    <Modal show={show} onClose={onModalClose} id="product-details" className={classes.modalSpacing}>
      <div className={classes.modalBody}>
        <h2 className={classes.title}>Product Details</h2>
        <h3 className={classes.actionBlockTitle}>What product line would you like to browse first?</h3>
        <p className={classes.actionBlockCaption}>(You can browse others later)</p>
        <div className={classes.actionBlock}>
          <div className={classes.rateSegmentSection}>
            <p className={classes.rateSegmentTitle}>{RATE_SEGMENT_INFO[currentRateSegment].title}</p>
            {getProductLinesButtons(pLineIds)?.length > 0 && (
              <ul className={classes.productLinesList} role="menu">
                {getProductLinesButtons(pLineIds)}
              </ul>
            )}
            {getAncillaryProductLinesButtons(pLineIds)?.length > 0 && (
              <div className={classes.ancillarySection}>
                <Button
                  type="outlined"
                  size="medium"
                  color="primary"
                  className={classes.ancillaryButton}
                  rightIconProp={showAncillaryPLines ? <ChevronUp /> : <ChevronDown />}
                  onClick={(): void => {
                    setShowAncillaryPLines(!showAncillaryPLines);
                  }}
                >
                  {getAncillaryButtonText()}
                </Button>
                {showAncillaryPLines && (
                  <ul className={classes.productLinesList} role="menu">
                    {getAncillaryProductLinesButtons(pLineIds)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
});

export default ProductDetailsModal;
